'use client';

import { useEffect, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { useAuthModal } from '~/components/auth/auth-modal-context';
import { CheckoutOverlay } from '~/components/billing/checkout-overlay';
import { useDisplayCurrency } from '~/components/currency-provider';
import { COIN_PURCHASES_ENABLED } from '~/config/monetization.config';
import { formatPrice } from '~/lib/currency';
import { getActiveSubscriptionSummary } from '~/lib/server/actions/billing-actions';

type SubscriptionFeature =
  | { type: 'unlimitedMessages' }
  | { type: 'coinsPerWeek'; count: number }
  | { type: 'coinsPerMonth'; count: number }
  | { type: 'coinsPerMonthWithTotal'; count: number; total: number }
  | { type: 'unblursPerWeek'; count: number }
  | { type: 'unblursPerMonth'; count: number }
  | { type: 'imagesPerWeek'; count: number }
  | { type: 'imagesPerMonth'; count: number }
  | { type: 'priorityGeneration' }
  | { type: 'fasterQueue' }
  | { type: 'fastestQueueWithEarly' };

type CoinPackageBadge = 'popular' | 'bestValue';
type PeriodKey = 'week' | 'month' | '3months';

// Coin packages - matching billing.config.ts. Display strings (name,
// description, badge) come from billing:pricing.coinPackages.* — only
// numbers / structural data live here.
const coinPackages: Array<{
  id: string;
  coins: number;
  price: number;
  originalPrice: number;
  featured: boolean;
  badge?: CoinPackageBadge;
}> = [
  {
    id: 'coins-700',
    coins: 700,
    price: 4.99,
    originalPrice: 6.99,
    featured: false,
  },
  {
    id: 'coins-1600',
    coins: 1600,
    price: 9.99,
    originalPrice: 13.99,
    badge: 'popular',
    featured: true,
  },
  {
    id: 'coins-3500',
    coins: 3500,
    price: 19.99,
    originalPrice: 27.99,
    featured: false,
  },
  {
    id: 'coins-8000',
    coins: 8000,
    price: 39.99,
    originalPrice: 54.99,
    badge: 'bestValue',
    featured: false,
  },
  {
    id: 'coins-18000',
    coins: 18000,
    price: 79.99,
    originalPrice: 109.99,
    featured: false,
  },
];

// Subscription plans - matching billing.config.ts. Display strings
// (name, description, badge, feature labels) come from
// billing:pricing.subscriptionPlans.* — only numbers and structured
// feature descriptors live here.
type PlanId = 'pass-weekly' | 'pass-monthly' | 'pass-quarterly';

const subscriptionPlans: Array<{
  id: PlanId;
  price: number;
  originalPrice: number;
  period: PeriodKey;
  coins: number;
  features: SubscriptionFeature[];
  featured: boolean;
}> = [
  {
    id: 'pass-weekly',
    price: 2.99,
    originalPrice: 3.99,
    period: 'week',
    coins: 500,
    features: [
      { type: 'unlimitedMessages' },
      { type: 'coinsPerWeek', count: 500 },
      { type: 'unblursPerWeek', count: 50 },
      { type: 'imagesPerWeek', count: 20 },
      { type: 'priorityGeneration' },
    ],
    featured: false,
  },
  {
    id: 'pass-monthly',
    price: 5.99,
    originalPrice: 8.99,
    period: 'month',
    coins: 1500,
    features: [
      { type: 'unlimitedMessages' },
      { type: 'coinsPerMonth', count: 1500 },
      { type: 'unblursPerMonth', count: 150 },
      { type: 'imagesPerMonth', count: 60 },
      { type: 'fasterQueue' },
    ],
    featured: true,
  },
  {
    id: 'pass-quarterly',
    price: 14.99,
    originalPrice: 19.99,
    period: '3months',
    coins: 7500,
    features: [
      { type: 'unlimitedMessages' },
      { type: 'coinsPerMonthWithTotal', count: 2000, total: 7500 },
      { type: 'unblursPerMonth', count: 250 },
      { type: 'imagesPerMonth', count: 100 },
      { type: 'fastestQueueWithEarly' },
    ],
    featured: false,
  },
];

function renderSubscriptionFeature(
  feature: SubscriptionFeature,
  t: (key: string, values?: Record<string, unknown>) => string,
): string {
  switch (feature.type) {
    case 'unlimitedMessages':
      return t('billing:pricing.subscriptionFeatures.unlimitedMessages');
    case 'priorityGeneration':
      return t('billing:pricing.subscriptionFeatures.priorityGeneration');
    case 'fasterQueue':
      return t('billing:pricing.subscriptionFeatures.fasterQueue');
    case 'fastestQueueWithEarly':
      return t('billing:pricing.subscriptionFeatures.fastestQueueWithEarly');
    case 'coinsPerMonthWithTotal':
      return t('billing:pricing.subscriptionFeatures.coinsPerMonthWithTotal', {
        count: feature.count,
        total: feature.total,
      });
    default:
      return t(`billing:pricing.subscriptionFeatures.${feature.type}`, {
        count: feature.count,
      });
  }
}

type TabType = 'coins' | 'subscribe';

// Hook to detect screen size
function useScreenSize() {
  const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'desktop'>(
    'desktop',
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setScreenSize('mobile');
      } else if (window.innerWidth < 1024) {
        setScreenSize('tablet');
      } else {
        setScreenSize('desktop');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
}

export default function PricingPage() {
  const { t } = useTranslation();
  const { currency } = useDisplayCurrency();
  const [activeTab, setActiveTab] = useState<TabType>(
    COIN_PURCHASES_ENABLED ? 'coins' : 'subscribe',
  );
  // Featured center card: 1600 coins on the coins tab, Monthly on subscribe.
  const [currentIndex, setCurrentIndex] = useState(
    COIN_PURCHASES_ENABLED ? 2 : 1,
  );
  const screenSize = useScreenSize();
  const { openAuthModal } = useAuthModal();

  const packages = activeTab === 'coins' ? coinPackages : subscriptionPlans;

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : packages.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < packages.length - 1 ? prev + 1 : 0));
  };

  const [buyLoading, setBuyLoading] = useState<string | null>(null);
  const [checkoutToken, setCheckoutToken] = useState<string | null>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  // Tracks the user's currently-active subscription so we can render
  // Current vs. "Switch to this plan" / "Resume Subscription" button
  // states on the subscribe tab. When the user has an active sub,
  // non-current plans become enabled "Switch" CTAs that open the
  // immediate-cancel trade-off modal. When the user's active sub is
  // already scheduled to cancel (`cancelAtPeriodEnd=true`), the
  // same-plan card becomes enabled too — labeled "Resume Subscription"
  // — so they can resume from /pricing instead of bouncing to
  // /account/billing. `null` = no active sub, signed out, or still
  // loading.
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [currentPeriodEndsAt, setCurrentPeriodEndsAt] = useState<string | null>(
    null,
  );
  const [currentCancelAtPeriodEnd, setCurrentCancelAtPeriodEnd] =
    useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    void getActiveSubscriptionSummary().then((summary) => {
      if (cancelled) return;
      setCurrentPlanId(summary?.planId ?? null);
      setCurrentPeriodEndsAt(summary?.periodEndsAt ?? null);
      setCurrentCancelAtPeriodEnd(summary?.cancelAtPeriodEnd ?? false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // When set, a confirmation modal asks the user to confirm the immediate
  // plan switch (old sub canceled now, no refund; new sub starts today).
  // The user clicks Confirm → handleBuyNow fires with switchFrom='current'.
  const [pendingSwitchPlanId, setPendingSwitchPlanId] = useState<string | null>(
    null,
  );

  const handleBuyNow = async (
    packageId: string,
    options?: { switchFromCurrent?: boolean },
  ) => {
    setBuyLoading(packageId);
    try {
      const response = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: packageId,
          ...(options?.switchFromCurrent ? { switchFrom: 'current' } : {}),
        }),
      });

      // Only treat 401 as "sign in needed" — every other error is a real
      // backend problem and the user already being signed in shouldn't be
      // told to sign in again.
      if (response.status === 401) {
        openAuthModal('sign-up', `/pricing`);
        return;
      }

      const data = (await response.json().catch(() => ({}))) as {
        checkoutToken?: string;
        error?: string;
        reason?: string;
        details?: unknown;
      };

      if (data.checkoutToken) {
        setCheckoutToken(data.checkoutToken);
        return;
      }

      // `data.error` is always a human sentence written by our route
      // ("Invalid promo code", "You are already subscribed…") and stays in
      // the alert. `data.details` is the raw gateway payload and goes ONLY to
      // the console — during the Aug-2026 checkout outage the alert showed
      // paying customers internals like `sku.0.price.offset: "Offset
      // parameter is not valid"`.
      const message =
        data.error ??
        t('billing:pricing.errors.checkoutFailed', {
          status: response.status,
        });
      console.error('[checkout] non-success response', {
        status: response.status,
        data,
      });
      alert(message);
    } catch (error) {
      // Network failure, JSON parse error, etc. — also not an auth issue.
      console.error('[checkout] network error', error);
      alert(t('billing:pricing.errors.networkError'));
    } finally {
      setBuyLoading(null);
    }
  };

  // Card click handler. Opens the confirm modal when the user has an
  // active sub and is either (a) clicking a different plan = switch, or
  // (b) clicking the same plan AND has cancel-at-period-end set =
  // resume by same-plan re-subscribe. Both branches end up posting to
  // /api/billing/checkout with `switchFrom: 'current'`; the server
  // allows same-plan switch only when cancel_at_period_end=true.
  const handleSubscriptionCardClick = (packageId: string) => {
    if (!currentPlanId) {
      void handleBuyNow(packageId);
      return;
    }
    const isDifferentPlan = currentPlanId !== packageId;
    const isSamePlanResume = !isDifferentPlan && currentCancelAtPeriodEnd;
    if (isDifferentPlan || isSamePlanResume) {
      setPendingSwitchPlanId(packageId);
      return;
    }
    // Same plan + not cancel-at-period-end → server will reject as
    // already_subscribed. Don't even fire the call.
    void handleBuyNow(packageId);
  };

  // Responsive card spacing
  const getCardSpacing = () => {
    switch (screenSize) {
      case 'mobile':
        return 140; // Smaller spacing for mobile
      case 'tablet':
        return 190;
      default:
        return 220;
    }
  };

  // How many cards to show on each side
  const getVisibleDistance = () => {
    switch (screenSize) {
      case 'mobile':
        return 1; // Only show 3 cards on mobile
      case 'tablet':
        return 2;
      default:
        return 2;
    }
  };

  const cardSpacing = getCardSpacing();
  const visibleDistance = getVisibleDistance();

  // The post-Buy modal is the shared CheckoutOverlay so it matches the
  // navbar BuyCoinsModal exactly. Rendered alongside the pricing UI so the
  // page background stays visible behind the dialog.
  const checkoutNode = (
    <CheckoutOverlay
      checkoutToken={checkoutToken}
      hideBack
      onBack={() => setCheckoutToken(null)}
      onComplete={() => setCheckoutToken(null)}
    />
  );

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0a0a0a]">
      {/* Purple glow effect at top */}
      <div className="pointer-events-none absolute top-0 left-1/2 h-[200px] w-[400px] -translate-x-1/2 rounded-full bg-purple-600/20 blur-[80px] sm:h-[300px] sm:w-[600px] sm:blur-[100px] lg:h-[400px] lg:w-[800px] lg:blur-[120px]" />

      {/* Hero Section */}
      <section className="relative z-10 bg-gradient-to-b from-purple-950/30 to-transparent pt-8 pb-2 sm:pt-12 sm:pb-4 lg:pt-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="mb-2 text-2xl font-bold text-white sm:mb-4 sm:text-3xl md:text-4xl lg:text-5xl">
            <Trans i18nKey="marketing:pricingPage.heroTitleStart" />{' '}
            <span className="bg-gradient-to-b from-pink-300 to-pink-500 bg-clip-text text-transparent">
              <Trans i18nKey="marketing:pricingPage.heroTitleAccent" />
            </span>
          </h1>
          <p className="mx-auto max-w-xl px-2 text-sm text-gray-400 sm:text-base lg:max-w-2xl lg:text-lg">
            <Trans i18nKey="marketing:pricingPage.heroSubheading" />
          </p>
        </div>
      </section>

      <div className="relative z-10 container mx-auto px-2 py-4 sm:px-4 sm:py-6 lg:py-8">
        {/* Heading hierarchy: plan/package cards render as h3, so the section
            needs an h2 between them and the hero h1 (SEO audit: "Missing H2").
            Screen-reader-only — the visual section label is the tab control. */}
        <h2 className="sr-only">
          Subscription plans and coin packages — Edem AI pricing
        </h2>
        {/* Tabs — hidden while coin purchases are off (subscriptions only) */}
        {COIN_PURCHASES_ENABLED && (
          <div className="mb-4 flex justify-center sm:mb-5 lg:mb-6">
            <div className="inline-flex rounded-full bg-gray-900/50 p-1">
              <button
                onClick={() => {
                  setActiveTab('coins');
                  setCurrentIndex(2);
                }}
                className={`rounded-full px-4 py-2 text-xs font-medium transition-all sm:px-6 sm:py-2.5 sm:text-sm ${
                  activeTab === 'coins'
                    ? 'bg-eros-pink text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Trans i18nKey="marketing:pricingPage.tabCoins" />
              </button>
              <button
                onClick={() => {
                  setActiveTab('subscribe');
                  setCurrentIndex(1);
                }}
                className={`rounded-full px-4 py-2 text-xs font-medium transition-all sm:px-6 sm:py-2.5 sm:text-sm ${
                  activeTab === 'subscribe'
                    ? 'bg-eros-pink text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Trans i18nKey="marketing:pricingPage.tabSubscribe" />
              </button>
            </div>
          </div>
        )}

        {/* Carousel — all screen sizes */}
        <div>
          <div
            className="relative flex items-center justify-center px-8 sm:px-12 lg:px-16"
            onTouchStart={(e) => setTouchStartX(e.touches[0]!.clientX)}
            onTouchEnd={(e) => {
              if (touchStartX === null) return;
              const diff = touchStartX - e.changedTouches[0]!.clientX;
              if (Math.abs(diff) > 50) {
                if (diff > 0) handleNext();
                else handlePrevious();
              }
              setTouchStartX(null);
            }}
          >
            {/* Previous Button */}
            <motion.button
              onClick={handlePrevious}
              whileHover={{
                scale: 1.1,
                backgroundColor: 'rgba(75, 85, 99, 0.5)',
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className="absolute left-0 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-gray-700 bg-gray-800/50 text-gray-400 transition-colors hover:text-white sm:left-2 sm:h-10 sm:w-10 lg:h-12 lg:w-12"
            >
              <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
            </motion.button>

            {/* Cards Container — absolute children contribute no height, so
                the min-h must fit the tallest card. Subscription cards run
                taller than coin cards (trial line + feature list), so the
                subscribe tab gets its own heights or the center card's
                button overlaps the dots below. */}
            <div
              className={`flex w-full items-center justify-center overflow-visible py-3 lg:py-4 ${
                activeTab === 'coins'
                  ? 'min-h-[380px] sm:min-h-[420px] lg:min-h-[450px]'
                  : 'min-h-[460px] sm:min-h-[500px] lg:min-h-[540px]'
              }`}
            >
              <AnimatePresence mode="popLayout" initial={false}>
                {packages.map((pkg, index) => {
                  const isCenter = index === currentIndex;

                  const distanceFromCenter = Math.min(
                    Math.abs(index - currentIndex),
                    Math.abs(index - currentIndex + packages.length),
                    Math.abs(index - currentIndex - packages.length),
                  );

                  const isVisible = distanceFromCenter <= visibleDistance;
                  if (!isVisible) return null;

                  let relativePosition = index - currentIndex;
                  if (relativePosition > packages.length / 2)
                    relativePosition -= packages.length;
                  if (relativePosition < -packages.length / 2)
                    relativePosition += packages.length;

                  const xOffset = relativePosition * cardSpacing;
                  const scale = isCenter
                    ? 1.05
                    : distanceFromCenter === 1
                      ? 0.85
                      : 0.7;
                  const opacity = isCenter
                    ? 1
                    : distanceFromCenter === 1
                      ? 0.5
                      : 0.25;
                  const blur = isCenter ? 0 : distanceFromCenter === 1 ? 1 : 3;

                  return (
                    <motion.div
                      key={pkg.id}
                      layout
                      initial={{ opacity: 0, scale: 0.8, x: xOffset }}
                      animate={{
                        opacity,
                        scale,
                        x: xOffset,
                        zIndex: 10 - distanceFromCenter,
                        filter: `blur(${blur}px)`,
                      }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30,
                        mass: 1,
                        opacity: { duration: 0.2 },
                      }}
                      // Side cards slide to center on click — users instinctively
                      // tap the package they want instead of the arrows (which
                      // drew rageclicks when only the arrows moved the carousel).
                      onClick={() => {
                        if (!isCenter) setCurrentIndex(index);
                      }}
                      className={`absolute flex-shrink-0 ${isCenter ? '' : 'cursor-pointer'}`}
                    >
                      {activeTab === 'coins' ? (
                        <CoinCard
                          pkg={pkg as (typeof coinPackages)[0]}
                          isCenter={isCenter}
                          onBuyNow={handleBuyNow}
                          screenSize={screenSize}
                          loadingId={buyLoading}
                        />
                      ) : (
                        <SubscriptionCard
                          plan={pkg as (typeof subscriptionPlans)[0]}
                          isCenter={isCenter}
                          onBuyNow={handleSubscriptionCardClick}
                          screenSize={screenSize}
                          loadingId={buyLoading}
                          currentPlanId={currentPlanId}
                          currentCancelAtPeriodEnd={currentCancelAtPeriodEnd}
                        />
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {/* Next Button */}
            <motion.button
              onClick={handleNext}
              whileHover={{
                scale: 1.1,
                backgroundColor: 'rgba(75, 85, 99, 0.5)',
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className="absolute right-0 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-gray-700 bg-gray-800/50 text-gray-400 transition-colors hover:text-white sm:right-2 sm:h-10 sm:w-10 lg:h-12 lg:w-12"
            >
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
            </motion.button>
          </div>

          {/* Dots Indicator */}
          <div className="mt-4 flex justify-center gap-2.5 sm:mt-6 sm:gap-2 lg:mt-8">
            {packages.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentIndex(index)}
                animate={{
                  width: index === currentIndex ? 24 : 8,
                  backgroundColor:
                    index === currentIndex ? '#ec4899' : '#4b5563',
                }}
                whileHover={{
                  backgroundColor:
                    index === currentIndex ? '#ec4899' : '#6b7280',
                }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                className="h-2.5 rounded-full sm:h-2"
              />
            ))}
          </div>

          {/* Existing-subscriber hint: picking a different plan triggers
              an immediate switch (old sub canceled now, no refund — modal
              confirms before charging). To preserve the current plan
              instead, the user uses Resume Subscription on /account/billing. */}
          {activeTab === 'subscribe' && currentPlanId && (
            <p className="mt-3 px-4 text-center text-xs text-gray-400 sm:mt-4 sm:text-sm">
              <Trans i18nKey="marketing:pricingPage.subscriberHintBefore" />
              <a
                href="/account/billing"
                className="text-eros-pink hover:text-eros-pink/80 underline"
              >
                <Trans i18nKey="marketing:pricingPage.subscriberHintLink" />
              </a>
              <Trans i18nKey="marketing:pricingPage.subscriberHintAfter" />
            </p>
          )}

          {/* Bank statement notice */}
          <p className="mt-4 text-center text-[11px] text-gray-500 sm:mt-6 sm:text-xs">
            <Trans i18nKey="marketing:pricingPage.bankStatementBefore" />
            <strong className="text-gray-400">
              <Trans i18nKey="marketing:pricingPage.bankStatementBrand" />
            </strong>
            <Trans i18nKey="marketing:pricingPage.bankStatementAfter" />
          </p>

          {/* Brand-fact prose. The page used to render 179 words total —
              plan cards with zero liftable sentences — so assistants
              answering "how much does Edem AI cost?" (the highest-CTR brand
              query class per GSC) had nothing to quote. These are
              self-contained ~140-word answer blocks. */}
          <section className="mx-auto mt-14 max-w-3xl space-y-10 text-left">
            <div>
              <h2 className="text-xl font-bold text-white sm:text-2xl">
                How much does Edem AI cost?
              </h2>
              <p className="mt-3 leading-relaxed text-gray-400">
                Edem AI is free to start: every new account gets 100 free coins
                with no credit card required, and the free tier never converts
                into a paid plan on its own. When you want unlimited messaging,
                there are three subscriptions — a Weekly Pass at $2.99 per week,
                Monthly at $5.99 per month, and a 3-Month Bundle at $14.99 for
                the full quarter. Every plan includes unlimited chat plus a coin
                allowance for extras like custom photo generation, photo
                unblurs, and voice or video calls. There is no per-message token
                system: your messages are never metered on a paid plan, so the
                advertised price is the real monthly cost. Prices are shown in
                your local currency at checkout.
              </p>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white sm:text-2xl">
                How does billing work?
              </h2>
              <p className="mt-3 leading-relaxed text-gray-400">
                Payments are processed by Centrobill, and every charge appears
                on your card statement under the discreet descriptor
                centrohelp.com — nothing on your bank statement mentions Edem AI
                or anything adult. Subscriptions renew automatically at the same
                price until you cancel, which you can do at any time in a couple
                of clicks from your account&apos;s billing settings; after
                cancelling you keep access until the end of the period you
                already paid for, and no further charges occur. Purchases are
                final and non-refundable, so if a charge ever looks wrong, email
                support@edemai.org before disputing it with your bank — we
                respond within 24–48 hours and resolve billing problems
                directly.
              </p>
            </div>
          </section>
        </div>
      </div>

      {/* Switch-plan confirmation modal. Shown only when the user has an
          active sub and clicked a different plan. The trade-off (lose
          remaining paid time) is explicit so the user can back out. */}
      {pendingSwitchPlanId &&
        (() => {
          const targetPlan = subscriptionPlans.find(
            (p) => p.id === pendingSwitchPlanId,
          );
          const currentPlan = subscriptionPlans.find(
            (p) => p.id === currentPlanId,
          );
          if (!targetPlan) return null;

          const todayLabel = new Date().toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });

          // Same-plan path: user is resuming a cancel-at-period-end sub on
          // the plan they're already on. Copy reads "Resume" instead of
          // "Switch" so the dialog matches user intent. Different-plan
          // path: keep the existing Switch language.
          const isResume = currentPlanId === targetPlan.id;
          const targetPlanName = t(
            `billing:pricing.subscriptionPlans.${targetPlan.id}.name`,
          );
          const currentPlanName = currentPlan
            ? t(`billing:pricing.subscriptionPlans.${currentPlan.id}.name`)
            : '';
          const targetPeriod = t(
            `billing:pricing.periods.${targetPlan.period}`,
          );
          const titleText = isResume
            ? t('billing:pricing.switchModal.resumeTitle', {
                planName: targetPlanName,
              })
            : t('billing:pricing.switchModal.switchTitle', {
                planName: targetPlanName,
              });

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
              <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#13111c] p-5 sm:p-6">
                <h3 className="text-base font-semibold text-white sm:text-lg">
                  {titleText}
                </h3>
                {isResume ? (
                  <p className="mt-3 text-sm text-gray-300">
                    <Trans i18nKey="billing:pricing.switchModal.resumeDescriptionBefore" />
                    <span className="font-semibold text-white">
                      {targetPlanName}
                    </span>
                    <Trans i18nKey="billing:pricing.switchModal.resumeDescriptionAfter" />
                  </p>
                ) : currentPlan ? (
                  <p className="mt-3 text-sm text-gray-300">
                    <Trans i18nKey="billing:pricing.switchModal.switchDescriptionBefore" />
                    <span className="font-semibold text-white">
                      {currentPlanName}
                    </span>
                    <Trans i18nKey="billing:pricing.switchModal.switchDescriptionAfter" />
                  </p>
                ) : null}
                <p className="mt-2 text-sm text-gray-300">
                  <Trans i18nKey="billing:pricing.switchModal.chargeBefore" />
                  <span className="font-semibold text-white">
                    {formatPrice(targetPlan.price, currency)}
                  </span>
                  <Trans
                    i18nKey="billing:pricing.switchModal.chargeMiddle"
                    values={{ date: todayLabel }}
                  />
                  <span className="font-semibold text-white">
                    {targetPlanName}
                  </span>
                  <Trans i18nKey="billing:pricing.switchModal.chargeAfter" />
                  <span className="font-semibold text-white">
                    {formatPrice(targetPlan.price, currency)}
                  </span>
                  <Trans
                    i18nKey="billing:pricing.switchModal.chargeRecurring"
                    values={{ period: targetPeriod }}
                  />
                </p>

                <div className="mt-5 flex justify-end gap-2">
                  <button
                    onClick={() => setPendingSwitchPlanId(null)}
                    disabled={!!buyLoading}
                    className="rounded-lg bg-white/5 px-3 py-2 text-xs font-medium text-gray-300 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50 sm:px-4 sm:text-sm"
                  >
                    <Trans i18nKey="billing:pricing.switchModal.cancelButton" />
                  </button>
                  <button
                    onClick={() => {
                      const planId = pendingSwitchPlanId;
                      setPendingSwitchPlanId(null);
                      void handleBuyNow(planId, { switchFromCurrent: true });
                    }}
                    disabled={!!buyLoading}
                    className="from-eros-pink rounded-lg bg-gradient-to-r to-rose-500 px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 sm:px-4 sm:text-sm"
                  >
                    <Trans
                      i18nKey={
                        isResume
                          ? 'billing:pricing.switchModal.confirmResume'
                          : 'billing:pricing.switchModal.confirmSwitch'
                      }
                    />
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {checkoutNode}
    </div>
  );
}

interface CoinCardProps {
  pkg: (typeof coinPackages)[0];
  isCenter: boolean;
  onBuyNow: (id: string) => void;
  screenSize: 'mobile' | 'tablet' | 'desktop';
  loadingId?: string | null;
}

function CoinCard({
  pkg,
  isCenter,
  onBuyNow,
  screenSize,
  loadingId,
}: CoinCardProps) {
  const { t } = useTranslation();
  const { currency } = useDisplayCurrency();
  const cardWidth =
    screenSize === 'mobile'
      ? 'w-[180px]'
      : screenSize === 'tablet'
        ? 'w-[240px]'
        : 'w-[280px]';

  return (
    <div
      className={`relative ${cardWidth} overflow-hidden rounded-2xl transition-all ${
        isCenter
          ? 'ring-eros-pink shadow-[0_0_30px_rgba(236,72,153,0.3)] ring-2 sm:shadow-[0_0_40px_rgba(236,72,153,0.3)]'
          : ''
      }`}
    >
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/40 via-gray-900 to-gray-900" />

      {/* Badge */}
      {pkg.badge && (
        <div className="relative">
          <div className="absolute top-0 right-0 left-0 flex justify-center">
            <div className="from-eros-pink rounded-b-lg bg-gradient-to-r to-rose-500 px-3 py-1 text-[10px] font-bold text-white sm:px-4 sm:py-1.5 sm:text-xs">
              {t(`billing:pricing.coinPackages.badges.${pkg.badge}`)}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div
        className={`relative p-4 sm:p-5 lg:p-6 ${pkg.badge ? 'pt-10 sm:pt-12' : 'pt-4 sm:pt-6'} text-center`}
      >
        {/* Package Name */}
        <h3 className="mb-0.5 text-base font-bold text-white sm:mb-1 sm:text-lg lg:text-xl">
          {t('billing:pricing.coinPackages.name', { count: pkg.coins })}
        </h3>
        <p className="mb-4 text-xs text-gray-400 sm:mb-6 sm:text-sm">
          {t(`billing:pricing.coinPackages.descriptions.${pkg.id}`)}
        </p>

        {/* Coins Amount */}
        <div className="mb-4 sm:mb-6">
          <span className="bg-gradient-to-b from-pink-300 to-pink-500 bg-clip-text text-3xl font-bold text-transparent sm:text-4xl lg:text-5xl">
            {pkg.coins.toLocaleString()}
          </span>
          <p className="mt-0.5 text-[10px] tracking-widest text-gray-400 sm:mt-1 sm:text-xs">
            <Trans i18nKey="billing:pricing.coinsLabel" />
          </p>
        </div>

        {/* Price */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-baseline justify-center gap-1.5">
            <span className="text-sm text-gray-500 line-through sm:text-base">
              {formatPrice(pkg.originalPrice, currency)}
            </span>
            <span className="text-2xl font-bold text-white sm:text-3xl">
              {formatPrice(pkg.price, currency)}
            </span>
          </div>
          <span className="mt-1 inline-block rounded-full bg-emerald-400/10 px-2 py-0.5 text-[10px] font-bold text-emerald-400 sm:text-xs">
            {t('billing:pricing.save', {
              percent: Math.round((1 - pkg.price / pkg.originalPrice) * 100),
            })}
          </span>
        </div>

        {/* Buy Button */}
        {isCenter && (
          <motion.button
            onClick={() => onBuyNow(pkg.id)}
            disabled={!!loadingId}
            whileHover={{
              scale: 1.03,
              boxShadow: '0 10px 40px rgba(236, 72, 153, 0.4)',
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            className="from-eros-pink w-full rounded-xl bg-gradient-to-r to-rose-500 py-2.5 text-sm font-semibold text-white disabled:opacity-60 sm:py-3 sm:text-base"
          >
            {loadingId === pkg.id ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <Trans i18nKey="billing:pricing.processing" />
              </span>
            ) : (
              <Trans i18nKey="billing:pricing.buyNow" />
            )}
          </motion.button>
        )}
      </div>
    </div>
  );
}

interface SubscriptionCardProps {
  plan: (typeof subscriptionPlans)[0];
  isCenter: boolean;
  onBuyNow: (id: string) => void;
  screenSize: 'mobile' | 'tablet' | 'desktop';
  loadingId?: string | null;
  currentPlanId?: string | null;
  currentCancelAtPeriodEnd?: boolean;
}

/**
 * Compute button state — disabled flag + i18n key for the label.
 *
 *   - no active sub                  → "Subscribe Now" (enabled)
 *   - same plan, normal active sub   → "Current Plan" (disabled)
 *   - same plan, cancel-at-period-end → "Resume Subscription" (enabled)
 *   - different plan                 → "Switch to this plan" (enabled)
 *
 * Returns the key (`labelKey`) instead of a translated string so the
 * caller stays in JSX-Trans land.
 */
function getSubscriptionButtonState(
  plan: (typeof subscriptionPlans)[0],
  currentPlanId: string | null | undefined,
  currentCancelAtPeriodEnd: boolean | undefined,
): { labelKey: string; disabled: boolean } {
  if (!currentPlanId) {
    return { labelKey: 'billing:pricing.subscribeNow', disabled: false };
  }

  if (currentPlanId === plan.id) {
    if (currentCancelAtPeriodEnd) {
      return {
        labelKey: 'billing:pricing.resumeSubscription',
        disabled: false,
      };
    }
    return { labelKey: 'billing:pricing.currentPlan', disabled: true };
  }

  return { labelKey: 'billing:pricing.switchToPlan', disabled: false };
}

function SubscriptionCard({
  plan,
  isCenter,
  onBuyNow,
  screenSize,
  loadingId,
  currentPlanId,
  currentCancelAtPeriodEnd,
}: SubscriptionCardProps) {
  const { t } = useTranslation();
  const { currency } = useDisplayCurrency();
  const cardWidth =
    screenSize === 'mobile'
      ? 'w-[180px]'
      : screenSize === 'tablet'
        ? 'w-[240px]'
        : 'w-[280px]';

  return (
    <div
      className={`relative ${cardWidth} overflow-hidden rounded-2xl transition-all ${
        isCenter
          ? 'ring-eros-pink shadow-[0_0_30px_rgba(236,72,153,0.3)] ring-2 sm:shadow-[0_0_40px_rgba(236,72,153,0.3)]'
          : ''
      }`}
    >
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/40 via-gray-900 to-gray-900" />

      {/* Badge */}
      <div className="relative">
        <div className="absolute top-0 right-0 left-0 flex justify-center">
          <div className="from-eros-pink rounded-b-lg bg-gradient-to-r to-rose-500 px-3 py-1 text-[10px] font-bold text-white sm:px-4 sm:py-1.5 sm:text-xs">
            {t(`billing:pricing.subscriptionPlans.${plan.id}.badge`)}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative p-3 pt-8 text-center sm:p-5 sm:pt-12 lg:p-6">
        {/* Plan Name */}
        <h3 className="mb-0.5 text-base font-bold text-white sm:mb-1 sm:text-lg lg:text-xl">
          {t(`billing:pricing.subscriptionPlans.${plan.id}.name`)}
        </h3>
        <p className="mb-2 text-xs text-gray-400 sm:mb-4 sm:text-sm">
          {t(`billing:pricing.subscriptionPlans.${plan.id}.description`)}
        </p>

        {/* Price */}
        <div className="mb-2 sm:mb-4">
          <span className="block text-sm text-gray-500 line-through sm:text-base">
            {formatPrice(plan.originalPrice, currency)}
          </span>
          <span className="bg-gradient-to-b from-pink-300 to-pink-500 bg-clip-text text-3xl font-bold text-transparent sm:text-4xl lg:text-5xl">
            {formatPrice(plan.price, currency)}
          </span>
          <p className="mt-0.5 text-[10px] tracking-widest text-gray-400 sm:mt-1 sm:text-xs">
            {t('billing:pricing.perPeriod', {
              period: t(`billing:pricing.periods.${plan.period}`).toUpperCase(),
            })}
          </p>
          <span className="mt-1 inline-block rounded-full bg-emerald-400/10 px-2 py-0.5 text-[10px] font-bold text-emerald-400 sm:text-xs">
            {t('billing:pricing.save', {
              percent: Math.round((1 - plan.price / plan.originalPrice) * 100),
            })}
          </span>

          {/* Trial — subscriptions only, never on coin packs */}
          <p className="mt-1.5 text-[11px] font-semibold text-pink-300 italic sm:text-xs">
            3-day free trial for new members
          </p>
        </div>

        {/* Features */}
        <ul className="mb-2 space-y-0.5 text-left text-xs text-gray-400 sm:mb-4 sm:space-y-1.5 sm:text-sm">
          {plan.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-1.5 sm:gap-2">
              <span className="text-eros-pink mt-0.5 text-xs sm:text-sm">
                ✓
              </span>
              <span className="text-[11px] sm:text-sm">
                {renderSubscriptionFeature(feature, t)}
              </span>
            </li>
          ))}
        </ul>

        {/* Buy Button */}
        {isCenter &&
          (() => {
            const buttonState = getSubscriptionButtonState(
              plan,
              currentPlanId,
              currentCancelAtPeriodEnd,
            );
            const isDisabled = buttonState.disabled || !!loadingId;

            return (
              <motion.button
                onClick={() => {
                  if (!buttonState.disabled) onBuyNow(plan.id);
                }}
                disabled={isDisabled}
                whileHover={
                  buttonState.disabled
                    ? undefined
                    : {
                        scale: 1.03,
                        boxShadow: '0 10px 40px rgba(236, 72, 153, 0.4)',
                      }
                }
                whileTap={buttonState.disabled ? undefined : { scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className={`w-full rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-60 sm:py-3 sm:text-base ${
                  buttonState.disabled
                    ? 'cursor-not-allowed bg-gray-700'
                    : 'from-eros-pink bg-gradient-to-r to-rose-500'
                }`}
              >
                {loadingId === plan.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <Trans i18nKey="billing:pricing.processing" />
                  </span>
                ) : (
                  <Trans i18nKey={buttonState.labelKey} />
                )}
              </motion.button>
            );
          })()}
      </div>
    </div>
  );
}
